import React, { createContext, useContext, useState, useCallback } from 'react'

/**
 * Holds the customer's delivery-time selection from the header.
 *  - mode: 'now' (ASAP) or 'scheduled'
 *  - scheduledFor: a Date (only when mode === 'scheduled')
 *
 * Consumers:
 *  - Header TimeSelector — read + write
 *  - Checkout — uses getOrderDate() as the placeOrder.orderDate
 */

const TimeContext = createContext(null)

export const TimeProvider = ({ children }) => {
  const [mode, setMode] = useState('now') // 'now' | 'scheduled'
  const [scheduledFor, setScheduledFor] = useState(null) // Date | null

  const selectNow = useCallback(() => {
    setMode('now')
    setScheduledFor(null)
  }, [])

  const selectScheduled = useCallback((date) => {
    setMode('scheduled')
    setScheduledFor(date instanceof Date ? date : new Date(date))
  }, [])

  // Returns a Date that Checkout should use as the order's orderDate.
  // For "now" we still need a real timestamp for the mutation.
  const getOrderDate = useCallback(() => {
    if (mode === 'scheduled' && scheduledFor) return scheduledFor
    return new Date()
  }, [mode, scheduledFor])

  return (
    <TimeContext.Provider value={{ mode, scheduledFor, selectNow, selectScheduled, getOrderDate }}>
      {children}
    </TimeContext.Provider>
  )
}

export const useTimeSelect = () => {
  const ctx = useContext(TimeContext)
  if (!ctx) throw new Error('useTimeSelect must be used inside <TimeProvider>')
  return ctx
}

export default TimeContext
